import React, { useState, useEffect, useRef } from 'react';
import {
  FileSpreadsheet, Upload, Plus, Check, X, Trash2, Wand2, Loader2, ShieldCheck, RefreshCw,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../../lib/api';
import { normTxt } from '../../lib/offline';
import { mergeTemoignages } from '../../lib/temoignages';
import { MapSelect } from '../Shared';
import SynthesisReview from './SynthesisReview';

/* Import des exports Google Forms — parsing xlsx côté navigateur (logique validée
   en maquette), application au persona et traçabilité via l'API. */

const SKIP_COL = /horodat|timestamp|e-?mail|adresse|score/i;
const REL_COL = /je suis/i;
const TITLE_COL = /titre/i;
const BODY_COL = /le souvenir|anecdote|souvenir|racont/i;
const CONF_COL = /confidentialit/i;
// Colonnes qui situent le souvenir : reprises en contexte sous le récit.
const CONTEXT_COL = /epoque|époque|date|lieu|contexte|qui était|qui etait|présent|present|ton de/i;

/* Le formulaire propose trois niveaux de confidentialité. Une anecdote « Haute »
   ne doit jamais alimenter le prompt : elle reste consultable en base mais n'est
   pas racontable. Voir Anecdote::CONFIDENTIALITIES côté serveur. */
const CONFIDENTIALITY_MAP = [
  [/^haute|confidentiel/i, 'rouge'],
  [/^moyenne|maitris|maîtris/i, 'orange'],
  [/^basse|racontable/i, 'vert'],
];

function mapConfidentiality(value) {
  const v = String(value || '').trim();
  if (!v) return 'vert';
  const hit = CONFIDENTIALITY_MAP.find(([re]) => re.test(v));
  return hit ? hit[1] : 'orange'; // valeur inattendue : on reste prudent
}

const CONFIDENTIALITY_LABEL = { vert: 'racontable', orange: 'ton maîtrisé', rouge: 'confidentielle — hors prompt' };

const cellOf = (r, ci) => String(r?.[ci] ?? '').trim();

/* Transformation pure d'un tableau (entêtes + lignes) en données de persona.
   Partagée par l'aperçu manuel et la synchronisation en un clic : les deux
   chemins produisent donc rigoureusement le même résultat. */

export function detectColumns(headers) {
  return {
    colRel: headers.findIndex((h) => REL_COL.test(h)),
    colTitle: headers.findIndex((h) => TITLE_COL.test(h)),
    colConf: headers.findIndex((h) => CONF_COL.test(h)),
    colContext: headers
      .map((h, ci) => (CONTEXT_COL.test(h) && !CONF_COL.test(h) && !SKIP_COL.test(h) && !TITLE_COL.test(h) ? ci : -1))
      .filter((ci) => ci >= 0),
  };
}

export function detectBodyColumn(headers, rows) {
  const named = headers.findIndex((h) => BODY_COL.test(h) && !TITLE_COL.test(h) && !CONF_COL.test(h));
  if (named >= 0) return named;
  let best = -1;
  let bestLen = 0;
  headers.forEach((h, ci) => {
    if (SKIP_COL.test(h) || REL_COL.test(h) || TITLE_COL.test(h) || CONF_COL.test(h)) return;
    const avg = rows.reduce((s, r) => s + cellOf(r, ci).length, 0) / Math.max(1, rows.length);
    if (avg > bestLen) { bestLen = avg; best = ci; }
  });
  return best;
}

export function buildAnecdotes(headers, rows, cols) {
  const { colText, colTitle, colRel, colConf, colContext } = cols;
  if (colText < 0) return [];
  return rows.map((r, i) => {
    const story = cellOf(r, colText);
    const context = colContext
      .map((ci) => [headers[ci]?.replace(/\s*\?\s*$/, '').trim(), cellOf(r, ci)])
      .filter(([, v]) => v)
      .map(([label, v]) => `${label} : ${v}`)
      .join(' — ');
    return {
      i,
      story,
      context,
      text: context ? `${story}\n\n(${context})` : story,
      title: colTitle >= 0 ? cellOf(r, colTitle) : '',
      source: colRel >= 0 ? cellOf(r, colRel) : '',
      confidentiality: mapConfidentiality(colConf >= 0 ? cellOf(r, colConf) : ''),
    };
  }).filter((p) => p.story);
}

export function aggregateQuestionnaire(headers, rows) {
  return headers.map((h, ci) => ({ h, ci }))
    .filter(({ h }) => h && !SKIP_COL.test(h) && !REL_COL.test(h))
    .map(({ h, ci }) => ({ q: h, answers: [...new Set(rows.map((r) => cellOf(r, ci)).filter(Boolean))] }))
    .filter((x) => x.answers.length);
}

export function toPersonaAnecdote(p) {
  return {
    title: p.title || (p.source ? `Raconté par ${p.source}` : 'Souvenir importé'),
    body: p.text,
    source: p.source || null,
    imported: true,
    confidentiality: p.confidentiality,
  };
}

export default function AdminImport() {
  const [persona, setPersona] = useState(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState('anecdotes');
  const [colText, setColText] = useState(-1);
  const [colTitle, setColTitle] = useState(-1);
  const [colRel, setColRel] = useState(-1);
  const [colConf, setColConf] = useState(-1);
  const [colContext, setColContext] = useState([]);
  const [selected, setSelected] = useState([]);
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const [synthState, setSynthState] = useState(null);
  const [synthMsg, setSynthMsg] = useState('');
  const [synthResult, setSynthResult] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(null);
  const [sheetsReady, setSheetsReady] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/api/admin/persona').then(setPersona).catch((e) => setErr(e.message));
    api.get('/api/admin/settings').then((s) => setSheetsReady(s.google_sheets)).catch(() => {});
  }, []);

  const cell = (r, ci) => String(r[ci] ?? '').trim();

  // Détection des colonnes et présélection, communes au fichier et à Google Sheets.
  const analyze = (hd, body, sourceName, forcedKind) => {
    setHeaders(hd);
    setRows(body);
    setFileName(sourceName);

    const isAnec = forcedKind
      ? forcedKind === 'anecdotes'
      : hd.some((h) => /anecdote|souvenir|histoire|racont/i.test(h)) || /anecdote/i.test(sourceName);
    setKind(isAnec ? 'anecdotes' : 'questionnaire');

    const cols = detectColumns(hd);
    setColRel(cols.colRel);
    setColTitle(cols.colTitle);
    setColConf(cols.colConf);
    setColContext(cols.colContext);
    setColText(detectBodyColumn(hd, body));
    setSelected(body.map(() => true));
  };

  // Charge un questionnaire directement depuis Google Sheets : même pipeline
  // d'aperçu et de déduplication que l'import de fichier.
  const loadFromSheets = async (sheetKind) => {
    setSheetBusy(sheetKind);
    setDone(''); setErr(''); setSynthState(null); setSynthResult(null);
    try {
      const data = await api.get(`/api/admin/google_sheets/${sheetKind}`);
      if (!data.rows.length) {
        setErr('Le classeur ne contient aucune réponse.');
        return;
      }
      analyze(data.headers.map(String), data.rows, `Google Sheets — ${sheetKind}`, sheetKind);
      setDone(`${data.rows.length} ligne${data.rows.length > 1 ? 's' : ''} chargée${data.rows.length > 1 ? 's' : ''} depuis Google Sheets.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSheetBusy(null);
    }
  };

  /* Synchronisation complète : récupère les deux classeurs et applique tout
     sans étape de validation. Sans danger : la déduplication écarte le déjà-vu,
     et les anecdotes confidentielles sont stockées mais restent exclues du
     prompt côté serveur. Les niveaux restent ajustables dans l'onglet Persona. */
  const syncAll = async () => {
    setSyncing(true);
    setDone(''); setErr(''); setSynthState(null); setSynthResult(null);
    const report = [];
    try {
      let current = persona;

      if (sheetsReady?.sheet_anecdotes_id) {
        const d = await api.get('/api/admin/google_sheets/anecdotes');
        const hd = d.headers.map(String);
        const cols = { ...detectColumns(hd), colText: detectBodyColumn(hd, d.rows) };
        const known = new Set((current.anecdotes || []).map((a) => normTxt(a.body || '')));
        const toAdd = buildAnecdotes(hd, d.rows, cols)
          .filter((p) => !known.has(normTxt(p.text)))
          .map(toPersonaAnecdote);
        if (toAdd.length) {
          current = await api.patch('/api/admin/persona', { anecdotes: [...current.anecdotes, ...toAdd] });
          await api.post('/api/admin/imports', {
            kind: 'anecdotes', filename: 'Google Sheets', rows_count: d.rows.length, imported_count: toAdd.length,
          });
        }
        const red = toAdd.filter((a) => a.confidentiality === 'rouge').length;
        report.push(toAdd.length
          ? `${toAdd.length} anecdote${toAdd.length > 1 ? 's' : ''} ajoutée${toAdd.length > 1 ? 's' : ''}${red ? ` (dont ${red} confidentielle${red > 1 ? 's' : ''}, hors prompt)` : ''}`
          : 'aucune nouvelle anecdote');
      }

      if (sheetsReady?.sheet_questionnaire_id) {
        const d = await api.get('/api/admin/google_sheets/questionnaire');
        const hd = d.headers.map(String);
        const { text, added } = mergeTemoignages(current.temoignages, aggregateQuestionnaire(hd, d.rows));
        if (added) {
          current = await api.patch('/api/admin/persona', { temoignages: text });
          await api.post('/api/admin/imports', {
            kind: 'questionnaire', filename: 'Google Sheets', rows_count: d.rows.length, imported_count: added,
          });
        }
        report.push(added
          ? `${added} réponse${added > 1 ? 's' : ''} de questionnaire ajoutée${added > 1 ? 's' : ''}`
          : 'aucune nouvelle réponse de questionnaire');
      }

      setPersona(current);
      setDone(`Synchronisation terminée — ${report.join(', ')}.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    setDone(''); setSynthState(null); setErr('');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    const hd = (arr[0] || []).map(String);
    const body = arr.slice(1).filter((r) => r.some((c) => String(c).trim()));
    analyze(hd, body, file.name);
  };

  /* --- anecdotes --- */
  const anecPreview = kind === 'anecdotes'
    ? buildAnecdotes(headers, rows, { colText, colTitle, colRel, colConf, colContext })
    : [];
  const existingTexts = new Set((persona?.anecdotes || []).map((a) => normTxt(a.body || '')));

  const applyAnecdotes = async () => {
    const toAdd = anecPreview
      .filter((p) => selected[p.i] && !existingTexts.has(normTxt(p.text)))
      .map(toPersonaAnecdote);
    setBusy(true);
    try {
      const data = await api.patch('/api/admin/persona', { anecdotes: [...persona.anecdotes, ...toAdd] });
      setPersona(data);
      await api.post('/api/admin/imports', {
        kind: 'anecdotes', filename: fileName, rows_count: rows.length, imported_count: toAdd.length,
      });
      setDone(`${toAdd.length} anecdote${toAdd.length > 1 ? 's' : ''} ajoutée${toAdd.length > 1 ? 's' : ''} à la mémoire du persona.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* --- questionnaire --- */
  const agg = kind === 'questionnaire' ? aggregateQuestionnaire(headers, rows) : [];

  const applyQuestionnaire = async () => {
    // Fusion par question, sans doublon : réimporter le même export n'ajoute rien.
    const { text, added } = mergeTemoignages(persona.temoignages, agg);
    setBusy(true);
    try {
      const data = await api.patch('/api/admin/persona', { temoignages: text });
      setPersona(data);
      await api.post('/api/admin/imports', {
        kind: 'questionnaire', filename: fileName, rows_count: rows.length, imported_count: added,
      });
      setDone(added === 0
        ? 'Aucune nouvelle réponse : ces témoignages étaient déjà intégrés.'
        : `${added} réponse${added > 1 ? 's' : ''} ajoutée${added > 1 ? 's' : ''} aux témoignages (${agg.length} questions).`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* --- synthèse IA : proposition côté serveur, application après relecture --- */
  const synthesize = async () => {
    setSynthState('busy');
    setSynthMsg('');
    setSynthResult(null);
    try {
      setSynthResult(await api.post('/api/admin/persona/synthesize'));
      setSynthState(null);
    } catch (e) {
      setSynthState('err');
      setSynthMsg(e.message || 'Échec de la synthèse.');
    }
  };

  const onSynthApplied = (updatedPersona, count) => {
    setPersona(updatedPersona);
    setSynthResult(null);
    setSynthState('ok');
    setSynthMsg(`${count} champ${count > 1 ? 's' : ''} appliqué${count > 1 ? 's' : ''} au persona. Ajustez-les si besoin dans l'onglet Persona.`);
  };

  const clearTemoignages = async () => {
    try {
      const data = await api.patch('/api/admin/persona', { temoignages: '' });
      setPersona(data);
    } catch (e) {
      setErr(e.message);
    }
  };

  if (!persona) return <div className="empty">{err || 'Chargement…'}</div>;

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <h3 className="panel-h"><FileSpreadsheet size={17} /> Importer les réponses des formulaires</h3>
        <p className="panel-p">
          Deux formulaires alimentent Oliv'IA, et ils ne servent pas à la même chose. Exportez les réponses
          Google Forms en Excel (Réponses → télécharger en .xlsx) puis déposez le fichier ici : le type est
          détecté automatiquement, et reste corrigeable ci-dessous.
        </p>
        <div className="forms-legend">
          <div className="form-kind">
            <div className="form-kind-title">Formulaire anecdotes</div>
            <div className="form-kind-desc">
              Une ligne = un souvenir raconté par un proche. Chaque ligne devient une anecdote dans la
              mémoire du persona, réécrite à la 1re personne au moment de répondre.
            </div>
          </div>
          <div className="form-kind">
            <div className="form-kind-title">Questionnaire descriptif</div>
            <div className="form-kind-desc">
              Une colonne = une question sur Olivier, une ligne = un proche qui répond. Les réponses
              alimentent les témoignages, matière première de la synthèse du persona.
            </div>
          </div>
        </div>

        {sheetsReady?.configured && (sheetsReady.sheet_anecdotes_id || sheetsReady.sheet_questionnaire_id) && (
          <div className="sheets-sync">
            <div className="sheets-sync-head">
              <RefreshCw size={15} />
              <span>Synchronisation directe — plus besoin de télécharger ni de déposer de fichier</span>
            </div>
            <div className="sheets-sync-actions">
              <button className="btn-primary" onClick={syncAll} disabled={syncing || sheetBusy !== null || !persona}>
                {syncing ? <><Loader2 size={15} className="spin" /> Synchronisation…</> : <><RefreshCw size={15} /> Tout synchroniser</>}
              </button>
              <button
                className="btn-ghost"
                onClick={() => loadFromSheets('anecdotes')}
                disabled={sheetBusy !== null || !sheetsReady.sheet_anecdotes_id}
                title={sheetsReady.sheet_anecdotes_id ? '' : 'Classeur non configuré dans les Réglages'}
              >
                {sheetBusy === 'anecdotes' ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
                Recharger les anecdotes
              </button>
              <button
                className="btn-ghost"
                onClick={() => loadFromSheets('questionnaire')}
                disabled={sheetBusy !== null || !sheetsReady.sheet_questionnaire_id}
                title={sheetsReady.sheet_questionnaire_id ? '' : 'Classeur non configuré dans les Réglages'}
              >
                {sheetBusy === 'questionnaire' ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
                Recharger le questionnaire
              </button>
            </div>
            <div className="hint left">
              <b>Tout synchroniser</b> applique directement les deux questionnaires, sans étape de
              validation. Les réponses déjà intégrées sont écartées, et les anecdotes marquées
              confidentielles sont enregistrées sans jamais alimenter le prompt — leur niveau reste
              modifiable dans l'onglet Persona. Les deux boutons suivants ouvrent l'aperçu détaillé
              si vous préférez trier avant d'appliquer.
            </div>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <div className="drop" onClick={() => fileRef.current?.click()}>
          <Upload size={20} />
          <span>{fileName || 'Choisir un fichier .xlsx / .csv'}</span>
          <span className="drop-sub">{rows.length ? `${rows.length} réponse${rows.length > 1 ? 's' : ''} détectée${rows.length > 1 ? 's' : ''}` : 'Export des réponses Google Forms'}</span>
        </div>

        {headers.length > 0 && (
          <>
            <div className="seg">
              <button className={`seg-btn${kind === 'anecdotes' ? ' active' : ''}`} onClick={() => setKind('anecdotes')}>Formulaire anecdotes</button>
              <button className={`seg-btn${kind === 'questionnaire' ? ' active' : ''}`} onClick={() => setKind('questionnaire')}>Questionnaire descriptif</button>
            </div>

            {kind === 'anecdotes' && (
              <>
                <div className="map-row">
                  <MapSelect label="Colonne récit" value={colText} onChange={setColText} headers={headers} />
                  <MapSelect label="Colonne titre (optionnel)" value={colTitle} onChange={setColTitle} headers={headers} allowNone />
                  <MapSelect label="Colonne « je suis » (optionnel)" value={colRel} onChange={setColRel} headers={headers} allowNone />
                  <MapSelect label="Colonne confidentialité" value={colConf} onChange={setColConf} headers={headers} allowNone />
                </div>
                {colConf < 0 && (
                  <div className="rz-note">
                    <ShieldCheck size={15} />
                    Aucune colonne de confidentialité repérée : tous les souvenirs seront importés comme
                    racontables. Sélectionnez-la si le formulaire en contient une, sinon vérifiez le niveau
                    de chaque anecdote dans l'onglet Persona.
                  </div>
                )}
                <div className="import-list">
                  {anecPreview.map((p) => {
                    const dup = existingTexts.has(normTxt(p.text));
                    return (
                      <label key={p.i} className={`import-item${dup ? ' dup' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!dup && !!selected[p.i]}
                          disabled={dup}
                          onChange={(e) => setSelected(selected.map((s, idx) => (idx === p.i ? e.target.checked : s)))}
                        />
                        <div>
                          <div className="import-title">
                            {p.title || (p.source ? `Raconté par ${p.source}` : 'Sans titre')}
                            <span className={`conf-chip ${p.confidentiality}`}>{CONFIDENTIALITY_LABEL[p.confidentiality]}</span>
                            {dup && <span className="dup-chip">déjà en mémoire</span>}
                          </div>
                          <div className="import-text">{p.story}</div>
                          {p.context && <div className="import-context">{p.context}</div>}
                        </div>
                      </label>
                    );
                  })}
                  {anecPreview.length === 0 && <div className="empty">Aucune anecdote détectée — vérifiez la colonne sélectionnée.</div>}
                </div>
                <button
                  className="btn-primary"
                  onClick={applyAnecdotes}
                  disabled={busy || !anecPreview.some((p) => selected[p.i] && !existingTexts.has(normTxt(p.text)))}
                >
                  {busy ? <Loader2 size={15} className="spin" /> : <Plus size={15} />} Ajouter à la mémoire du persona
                </button>
              </>
            )}

            {kind === 'questionnaire' && (
              <>
                <div className="import-list">
                  {agg.map((x, i) => (
                    <div key={i} className="import-item static">
                      <div>
                        <div className="import-title">{x.q}</div>
                        <div className="import-text">{x.answers.slice(0, 4).join(' · ')}{x.answers.length > 4 ? ` · +${x.answers.length - 4}` : ''}</div>
                      </div>
                    </div>
                  ))}
                  {agg.length === 0 && <div className="empty">Aucune question exploitable détectée dans ce fichier.</div>}
                </div>
                <button className="btn-primary" onClick={applyQuestionnaire} disabled={busy || !agg.length}>
                  {busy ? <Loader2 size={15} className="spin" /> : <Plus size={15} />} Intégrer aux témoignages du persona
                </button>
              </>
            )}
          </>
        )}

        {done && <div className="test ok"><Check size={15} /> {done}</div>}
        {err && <div className="test err"><X size={15} /> {err}</div>}
      </div>

      <div className="panel span2">
        <h3 className="panel-h"><Wand2 size={17} /> Synthèse IA du persona</h3>
        <p className="panel-p">
          Témoignages stockés : <b>{persona.temoignages ? `${persona.temoignages.length} caractères` : 'aucun'}</b> · Anecdotes en mémoire : <b>{persona.anecdotes.length}</b>.
          {persona.temoignages && (
            <button className="mini-btn danger inline-clear" onClick={clearTemoignages}><Trash2 size={13} /> vider les témoignages</button>
          )}
        </p>
        <p className="panel-p">
          La synthèse relit les témoignages et les anecdotes pour proposer un persona complet :
          identité (parcours, métier, histoire, passions, valeurs) et voix (ton, expressions, humour,
          traits, opinions, autodérision). Elle vous soumet une proposition — rien n'est écrit avant
          votre validation, champ par champ. Les zones rouges restent de votre seule décision.
        </p>
        {!synthResult && (
          <button
            className="btn-primary"
            onClick={synthesize}
            disabled={synthState === 'busy' || (!persona.temoignages && persona.anecdotes.length === 0)}
          >
            {synthState === 'busy' ? <><Loader2 size={15} className="spin" /> Analyse en cours…</> : <><Wand2 size={15} /> Proposer une synthèse du persona</>}
          </button>
        )}
        {synthResult && (
          <SynthesisReview
            result={synthResult}
            onApplied={onSynthApplied}
            onCancel={() => setSynthResult(null)}
          />
        )}
        {synthState === 'ok' && <div className="test ok"><Check size={15} /> {synthMsg}</div>}
        {synthState === 'err' && <div className="test err"><X size={15} /> {synthMsg}</div>}
      </div>
    </div>
  );
}
